import { createContext, useContext, useState, useEffect } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaptchaContext = createContext({
  executeRecaptcha: null,
  enabled: false,
  loading: true
});

export const useCaptcha = () => useContext(CaptchaContext);

// Inner component that uses the reCAPTCHA hook
function CaptchaProviderInner({ children }) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  const getToken = async (action) => {
    if (!executeRecaptcha) {
      console.log("reCAPTCHA not ready");
      return null;
    }
    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      return null;
    }
  };

  return (
    <CaptchaContext.Provider value={{ executeRecaptcha: getToken, enabled: true, loading: false }}>
      {children}
    </CaptchaContext.Provider>
  );
}

// Main provider that conditionally enables reCAPTCHA
export function CaptchaProvider({ children }) {
  const [config, setConfig] = useState({ enabled: false, site_key: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/config/captcha`);
      setConfig(res.data);
    } catch (error) {
      console.log("CAPTCHA config not available");
      setConfig({ enabled: false, site_key: null });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return children; // Render without captcha while loading config
  }

  if (!config.enabled || !config.site_key) {
    // CAPTCHA disabled - provide dummy context
    return (
      <CaptchaContext.Provider value={{ executeRecaptcha: async () => null, enabled: false, loading: false }}>
        {children}
      </CaptchaContext.Provider>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.site_key}>
      <CaptchaProviderInner>
        {children}
      </CaptchaProviderInner>
    </GoogleReCaptchaProvider>
  );
}

export default CaptchaProvider;
